import React, { useState, useEffect, useRef } from 'react'
import { 
  Plus,
  Star,
  Settings,
  TrendingUp,
  AlertTriangle,
  AlertCircle,
  Info,
  CheckCircle,
  MessageCircle
} from 'lucide-react'
import DataTable from '../../components/DataTable/DataTable'
import Breadcrumb from '../../components/Common/Breadcrumb'
import ConfirmationModal from '../../components/ConfirmationModal/ConfirmationModal'
import { useNavigate } from 'react-router-dom'
import { toast } from 'react-toastify'
import priorityMasterService from '../../services/priorityMasterService'
import RevisionHistoryModal from '../../components/RevisionHistoryModal/RevisionHistoryModal'
import '../../components/RevisionHistoryModal/RevisionHistoryModal.css'
import '../../components/ConfirmationModal/ConfirmationModal.css'
// Consolidated CSS is imported globally

const PriorityMaster = () => {
  const navigate = useNavigate()
  const [selectedRows, setSelectedRows] = useState([])
  const [data, setData] = useState([])
  const [loading, setLoading] = useState(true)
  const [stats, setStats] = useState({
    totalCount: 0,
    categoryStats: [],
    levelStats: [],
    recentCount: 0
  })
  const [showRevisionHistory, setShowRevisionHistory] = useState(false)
  const [showDeleteModal, setShowDeleteModal] = useState(false)
  const [deleteItem, setDeleteItem] = useState(null)
  const [selectedPriorityData, setSelectedPriorityData] = useState(null)
  const hasLoadedRef = useRef(false)
  const hasShownEmptyToastRef = useRef(false)

  // Load data from backend
  useEffect(() => {
    if (!hasLoadedRef.current) {
      hasLoadedRef.current = true
      loadData()
    }
  }, [])

  const loadData = async () => {
    try {
      setLoading(true)
      const [priorityResponse, statsResponse] = await Promise.all([
        priorityMasterService.getAllPriorityMasters(),
        priorityMasterService.getPriorityMasterStats()
      ])

      if (priorityResponse.success) {
        setData(priorityResponse.data)
        
        // Show toast if no data
        if (priorityResponse.data.length === 0 && !hasShownEmptyToastRef.current) {
          hasShownEmptyToastRef.current = true
          toast.info('No priority masters found. Create your first one!')
        }
      } else {
        toast.error(priorityResponse.message || 'Failed to load priority masters')
      }

      if (statsResponse.success) {
        setStats(statsResponse.data)
      }
    } catch (error) {
      console.error('Error loading data:', error)
      if (error.response?.status === 401) {
        toast.error('Authentication required. Please log in again.')
      } else if (error.response?.status === 403) {
        toast.error('You do not have permission to view priority masters.')
      } else {
        toast.error('Failed to load priority masters. Please try again.')
      }
    } finally {
      setLoading(false)
    }
  }

  const handleCreate = () => {
    navigate('/projects/priority-master/new')
  }

  const handleEdit = (row) => {
    // Clean the data to remove non-serializable objects
    const cleanRowData = {
      _id: row._id,
      id: row.id,
      company: row.company,
      priorityCode: row.priorityCode,
      priorityName: row.priorityName,
      priorityDescription: row.priorityDescription,
      priorityLevel: row.priorityLevel,
      priorityCategory: row.priorityCategory,
      priorityColor: row.priorityColor,
      priorityIcon: row.priorityIcon,
      isDefault: row.isDefault,
      sortOrder: row.sortOrder,
      responseTimeHours: row.responseTimeHours,
      resolutionTimeHours: row.resolutionTimeHours,
      escalationTimeHours: row.escalationTimeHours,
      notifyOnCreation: row.notifyOnCreation,
      notifyOnEscalation: row.notifyOnEscalation,
      notifyStakeholders: row.notifyStakeholders,
      businessImpact: row.businessImpact,
      urgencyLevel: row.urgencyLevel,
      averageResolutionTime: row.averageResolutionTime,
      escalationRate: row.escalationRate,
      revisionHistory: row.revisionHistory,
      currentRevision: row.currentRevision,
      createdBy: row.createdBy,
      updatedBy: row.updatedBy,
      isArchived: row.isArchived,
      createdAt: row.createdAt,
      updatedAt: row.updatedAt
    }
    
    navigate('/projects/priority-master/edit', { 
      state: { priorityData: cleanRowData } 
    })
  }

  const handleDelete = async (row) => {
    setDeleteItem(row)
    setShowDeleteModal(true)
  }

  const confirmDelete = async () => {
    if (!deleteItem) return

    try {
      const response = await priorityMasterService.deletePriorityMaster(deleteItem._id)
      
      if (response.success) {
        toast.success('Priority master deleted successfully!')
        loadData() // Reload data
      } else {
        toast.error(response.message || 'Failed to delete priority master')
      }
    } catch (error) {
      console.error('Error deleting priority master:', error)
      if (error.response?.status === 401) {
        toast.error('Authentication required. Please log in again.')
      } else if (error.response?.status === 403) {
        toast.error('You do not have permission to delete priority masters.')
      } else {
        toast.error('Failed to delete priority master. Please try again.')
      }
    } finally {
      setShowDeleteModal(false)
      setDeleteItem(null)
    }
  }

  const handleViewRevisions = (row) => {
    setSelectedPriorityData(row)
    setShowRevisionHistory(true)
  }

  const getPriorityIcon = (priorityCategory) => {
    switch (priorityCategory) {
      case 'Critical':
        return <AlertTriangle size={16} style={{ color: '#DC2626' }} />
      case 'High':
        return <AlertCircle size={16} style={{ color: '#F59E0B' }} />
      case 'Medium':
        return <Info size={16} style={{ color: '#3B82F6' }} />
      case 'Low':
        return <CheckCircle size={16} style={{ color: '#10B981' }} />
      case 'Informational':
        return <MessageCircle size={16} style={{ color: '#6B7280' }} />
      default:
        return <Star size={16} style={{ color: '#6B7280' }} />
    }
  }

  const getPriorityColor = (priorityColor) => {
    return priorityColor || '#6B7280'
  }

  const getPriorityLevelColor = (level) => {
    if (level >= 8) return '#DC2626' // Red for high
    if (level >= 6) return '#F59E0B' // Orange for medium-high
    if (level >= 4) return '#3B82F6' // Blue for medium
    if (level >= 2) return '#10B981' // Green for low
    return '#6B7280' // Gray for very low
  }

  // Define columns for the data table
  const columns = [
    {
      key: 'priorityCode',
      label: 'Priority Code',
      sortable: true,
      render: (value, row) => (
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <div 
            style={{ 
              width: '12px', 
              height: '12px', 
              borderRadius: '50%', 
              backgroundColor: getPriorityColor(row.priorityColor) 
            }} 
          />
          <span style={{ fontWeight: '600', fontFamily: 'monospace' }}>{value}</span>
        </div>
      )
    },
    {
      key: 'priorityName',
      label: 'Priority Name',
      sortable: true,
      render: (value, row) => (
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          {getPriorityIcon(row.priorityCategory)}
          <span style={{ fontWeight: '500' }}>{value}</span>
        </div>
      )
    },
    {
      key: 'priorityLevel',
      label: 'Level',
      sortable: true,
      render: (value) => (
        <span style={{ 
          padding: '4px 8px', 
          borderRadius: '4px', 
          fontSize: '12px',
          fontWeight: '600',
          backgroundColor: getPriorityLevelColor(value) + '20',
          color: getPriorityLevelColor(value)
        }}>
          {value}/10
        </span>
      )
    },
    {
      key: 'priorityCategory',
      label: 'Category',
      sortable: true,
      render: (value) => (
        <span style={{ 
          padding: '4px 8px', 
          borderRadius: '4px', 
          fontSize: '12px',
          fontWeight: '500',
          backgroundColor: '#f3f4f6',
          color: '#374151'
        }}>
          {value}
        </span>
      )
    },
    {
      key: 'responseTimeHours',
      label: 'Response Time',
      sortable: true,
      render: (value) => (
        <span style={{ 
          padding: '4px 8px', 
          borderRadius: '4px', 
          fontSize: '12px',
          fontWeight: '500',
          backgroundColor: '#E8E1F5',
          color: '#0369a1'
        }}>
          {value}h
        </span>
      )
    },
    {
      key: 'isDefault',
      label: 'Default',
      sortable: true,
      render: (value) => (
        value ? (
          <span style={{ 
            padding: '4px 8px', 
            borderRadius: '4px', 
            fontSize: '12px',
            fontWeight: '500',
            backgroundColor: '#dcfce7',
            color: '#166534'
          }}>
            Yes
          </span>
        ) : (
          <span style={{ 
            padding: '4px 8px', 
            borderRadius: '4px', 
            fontSize: '12px',
            fontWeight: '500',
            backgroundColor: '#f3f4f6',
            color: 'var(--color-text-secondary, #5B6478)'
          }}>
            No
          </span>
        )
      )
    }
  ]

  const breadcrumbItems = [
    { label: 'Projects', path: '/projects', onClick: () => navigate('/projects') },
    { label: 'Masters', path: '/projects?tab=masters', onClick: () => navigate('/projects?tab=masters') },
    { label: 'Priority Master', path: '/projects/priority-master', isCurrent: true }
  ]

  return (
    <div className="sales-dashboard">
      {/* Breadcrumb Navigation */}
      <div className="breadcrumb-section">
        <Breadcrumb items={breadcrumbItems} />
      </div>

      {/* Page Header */}
      <div className="page-header">
        <div className="page-title">
          <h1>Priority Master</h1>
          <p>Manage priority levels and escalation settings for projects</p>
        </div>
        <div className="page-actions">
          <button className="btn-primary" onClick={handleCreate}>
            <Plus size={16} />
            Add New
          </button>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="dashboard-metrics-grid">
        <div className="metric-card revenue">
          <div className="metric-header">
            <div className="metric-icon">
              <Star size={16} />
            </div>
            <div className="metric-trend positive">
              <TrendingUp size={12} />
              <span>+12%</span>
            </div>
          </div>
          <div className="metric-value">{stats.totalCount}</div>
          <div className="metric-title">Total Priorities</div>
          <div className="metric-subtitle">vs last month</div>
        </div>

        <div className="metric-card customers">
          <div className="metric-header">
            <div className="metric-icon">
              <TrendingUp size={16} />
            </div>
            <div className="metric-trend positive">
              <TrendingUp size={12} />
              <span>+8%</span>
            </div>
          </div>
          <div className="metric-value">{stats.recentCount}</div>
          <div className="metric-title">Recent (30 days)</div>
          <div className="metric-subtitle">Currently tracking</div>
        </div>

        <div className="metric-card churn">
          <div className="metric-header">
            <div className="metric-icon">
              <AlertTriangle size={16} />
            </div>
            <div className="metric-trend neutral">
              <TrendingUp size={12} />
              <span>Stable</span>
            </div>
          </div>
          <div className="metric-value">{stats.categoryStats.length}</div>
          <div className="metric-title">Categories</div>
          <div className="metric-subtitle">Active categories</div>
        </div>

        <div className="metric-card revenue">
          <div className="metric-header">
            <div className="metric-icon">
              <Settings size={16} />
            </div>
            <div className="metric-trend positive">
              <TrendingUp size={12} />
              <span>+5%</span>
            </div>
          </div>
          <div className="metric-value">{stats.levelStats.length}</div>
          <div className="metric-title">Levels</div>
          <div className="metric-subtitle">Priority levels</div>
        </div>
      </div>

      {/* Priority Master Table */}
      <div className="dashboard-content">
        <DataTable
          data={data}
          columns={columns}
          loading={loading}
          onEdit={handleEdit}
          onView={handleViewRevisions}
          onDelete={handleDelete}
          onRevisionHistory={handleViewRevisions}
          selectedRows={selectedRows}
          onSelectionChange={setSelectedRows}
          searchPlaceholder="Search priorities..."
          emptyMessage="No priority masters found. Create your first priority!"
          noCard={true}
        />
      </div>

      {/* Revision History Modal */}
      {showRevisionHistory && selectedPriorityData && (
        <RevisionHistoryModal
          isOpen={showRevisionHistory}
          onClose={() => setShowRevisionHistory(false)}
          data={selectedPriorityData}
          title="Priority Master Revision History"
        />
      )}

      {/* Delete Confirmation Modal */}
      <ConfirmationModal
        isOpen={showDeleteModal}
        onClose={() => setShowDeleteModal(false)}
        onConfirm={confirmDelete}
        title="Delete Priority Master"
        message={`Are you sure you want to delete "${deleteItem?.priorityName}"? This action cannot be undone.`}
        confirmText="Delete"
        cancelText="Cancel"
        type="danger"
      />
    </div>
  )
}

export default PriorityMaster

